import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addMonths } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { CalendarDatePickerField } from '@shared/ui/forms/CalendarDatePickerField';
import { Stepper } from '@shared/ui/lists/Stepper';
import { toast } from '@shared/ui/feedback/Toast';
import { toApiError } from '@shared/api/errors';
import { AccessInvitePropertyPicker } from '../AccessInvitePropertyPicker';
import { inviteCredentialsQueryKey } from '../InviteCredentialPass';
import { ACCESS_INVITES_QUERY_KEY } from '../accessInviteCache';
import { formatAccessInviteLocationLabel } from '../accessInviteLocationUi';
import type { EligibleLocation } from '../api';
import { MEMBER_CONTACTS_QUERY_KEY } from '../memberContactsApi';
import { gatePinsQueryKey, type MemberAppFeatures, type createBooking } from '../memberAppApi';
import { BOOKING_WINDOW_MONTHS, dayOf, startOfToday } from '../visitorAccessFormat';
import { FIELD_LABEL, HELP } from '../visitorAccessStyles';

/*
 * What the three bookings (delivery, guest stay, goods removal) share: a stepper in the Book sheet with
 * the step's button kept at the bottom, the property they are for, a day on the calendar, and making the PIN.
 */

/** A property this kind of booking is switched on for. */
export type BookingProperty = EligibleLocation & { features: MemberAppFeatures };

/** What each booking's steps get from the Book sheet. */
export interface BookingProps {
  properties: BookingProperty[];
  /** The PIN is made: the sheet closes and Active PINs shows it. */
  onCreated: () => void;
  /** A step ahead of the booking's own (New PIN's Type), tapped to go back to it. */
  lead?: BookingLead;
}

interface BookingLead {
  label: string;
  onBack: () => void;
}

interface BookingStep {
  label: string;
  title: string;
  description: string;
}

/**
 * A booking's steps, as Add a person shows them: where the member is (an earlier step can be tapped
 * to go back), the step's question, its fields, and its button pinned to the bottom of the sheet.
 */
export function BookingSteps({
  steps,
  step,
  onStep,
  lead,
  action,
  children,
}: {
  steps: BookingStep[];
  step: number;
  onStep: (index: number) => void;
  lead?: BookingLead;
  /** The step's button; a step chosen by tapping a row (a guest) has none. */
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const { title, description } = steps[step]!;
  // A lead step sits first and done; tapping it leaves the booking.
  const offset = lead ? 1 : 0;
  const labels = (lead ? [lead.label] : []).concat(steps.map(({ label }) => label));

  return (
    <div className="flex min-h-full flex-col gap-4.5">
      <Stepper
        total={labels.length}
        current={step + offset}
        labels={labels}
        onStepSelect={(index) => (lead && index === 0 ? lead.onBack() : onStep(index - offset))}
        isStepAccessible={(index) => index < step + offset}
      />
      <div className="flex flex-col gap-1">
        <p className="text-headline font-bold text-ink">{title}</p>
        <p className="text-[0.8125rem] leading-snug text-ink-secondary">{description}</p>
      </div>

      <div className={action ? 'flex flex-1 flex-col gap-4.5' : 'flex flex-1 flex-col gap-4.5 pb-4'}>{children}</div>

      {action ? <div className="sticky bottom-0 -mx-6 bg-elevated px-6 pb-4 pt-3 hairline-t">{action}</div> : null}
    </div>
  );
}

/** A labelled group on the form, with an optional line of help under it. */
export function BookingField({ label, helper, children }: { label: string; helper?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>{label}</span>
      {children}
      {helper ? <p className={HELP}>{helper}</p> : null}
    </div>
  );
}

/** What is picked (the property), bordered like the inputs, with a Change link on the right. */
const PICKED_CARD =
  'flex min-h-[52px] w-full items-center justify-between gap-3 rounded-[12px] border border-hairline bg-card px-3.5 text-left text-[15px] font-semibold leading-[normal] text-ink';
const CHANGE_LINK = 'shrink-0 text-[13px] font-semibold text-primary-500';

/** The property the booking is for. With several, Change offers the others. */
export function PropertyField({
  properties,
  value,
  onChange,
}: {
  properties: BookingProperty[];
  value: string;
  onChange: (locationId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const selected = properties.find((property) => property.location_id === value)!;
  const label = <span className="truncate">{formatAccessInviteLocationLabel(selected)}</span>;

  if (properties.length === 1) {
    return (
      <BookingField label="Property">
        <div className={PICKED_CARD}>{label}</div>
      </BookingField>
    );
  }

  return (
    <BookingField label="Property">
      <button type="button" className={PICKED_CARD} onClick={() => setOpen(true)}>
        {label}
        <span className={CHANGE_LINK}>Change</span>
      </button>
      <BottomSheet open={open} onOpenChange={setOpen} title="Pick the property">
        <div className="pb-4">
          <AccessInvitePropertyPicker
            locations={properties}
            selectedId={value}
            onSelect={(locationId) => {
              onChange(locationId);
              setOpen(false);
            }}
          />
        </div>
      </BottomSheet>
    </BookingField>
  );
}

interface DayProps {
  label: string;
  description: string;
  value: Date;
  min: Date;
  onChange: (day: Date) => void;
}

/** One calendar day under its label ("Sat 18 Oct"), with a line of help when given. */
export function DayField({ helper, ...props }: DayProps & { helper?: string }): JSX.Element {
  return (
    <BookingField label={props.label} helper={helper}>
      <DayButton {...props}>
        <span className="text-[15px] font-semibold">{dayOf(props.value)}</span>
      </DayButton>
    </BookingField>
  );
}

/** A day from `min` up to the booking window's end, picked on the shared calendar sheet. */
function DayButton({
  label,
  description,
  value,
  min,
  onChange,
  children,
}: DayProps & { children: ReactNode }): JSX.Element {
  const [month, setMonth] = useState(value);
  // The gate refuses a booking that starts more than the window ahead, so later days can't be picked.
  const latest = addMonths(startOfToday(), BOOKING_WINDOW_MONTHS);

  return (
    <CalendarDatePickerField
      label={label}
      description={description}
      helperText=""
      selectedDate={value}
      visibleMonth={month}
      startMonth={min}
      endMonth={latest}
      hasAvailableDays
      emptyMonthMessage=""
      isDateDisabled={(date) => date < min || date > latest}
      onMonthChange={setMonth}
      onSelectDate={onChange}
      trigger={(open) => (
        <button
          type="button"
          onClick={open}
          className="flex min-h-[58px] w-full items-center justify-between gap-2 rounded-[12px] border border-hairline bg-card pl-3.5 pr-3 text-left leading-[normal] text-ink"
        >
          {children}
          <CalendarDays className="h-5 w-5 shrink-0 text-primary-500" strokeWidth={2} aria-hidden />
        </button>
      )}
    />
  );
}

/**
 * Makes the PIN through the gate (up to a minute), then hands back so the sheet closes onto Active PINs.
 * On failure the member stays on the step with what they entered.
 */
export function useCreatePin<T extends { location_id: string }>(
  create: (payload: T) => ReturnType<typeof createBooking>,
  onCreated: () => void,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: create,
    onSuccess: ({ invite, credentials }, payload) => {
      // 1. Keep the PIN the gate returned, and refresh every list it now shows in.
      if (credentials) queryClient.setQueryData(inviteCredentialsQueryKey(invite.access_invite_id), credentials);
      void queryClient.invalidateQueries({ queryKey: ACCESS_INVITES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: gatePinsQueryKey(payload.location_id) });
      void queryClient.invalidateQueries({ queryKey: MEMBER_CONTACTS_QUERY_KEY });

      // 2. Over to Active PINs, where the new one is.
      toast.success('PIN created');
      onCreated();
    },
    onError: (error) => {
      // A timed-out request may still have made the PIN, so the member looks before trying again.
      const { message } = toApiError(error);
      toast.danger(
        "Couldn't create the PIN",
        /timeout/i.test(message)
          ? 'The PIN may have been made. Check Active PINs before you try again.'
          : message,
      );
    },
  });
}
